import {
	getAllReplyNotifications,
	getAllMentionNotifications,
	markNotificationAsRead
} from './chat.api';
import { writable } from 'svelte/store';

export interface ExtendedNotification {
	id: string;
	created_at: string;
	dismissed?: boolean;
	read?: boolean;
	type?: string;
	message?: string;
	post_slug?: string;
	post_id?: string;
	post_or_chat_id?: string;
	author_username?: string;
	medium?: string;
	count?: number;
	created_by?: string;
	channel_name?: string;
	replied_to_username?: string;
	mentioned?: string[];
}

// Singleton notification store
const notificationStore = writable<ExtendedNotification[]>([]);
let currentUsername: string | null = null;
let initialized = false;

export function initNotificationStore(username: string) {
	if (!username) return notificationStore;
	if (currentUsername !== username) {
		currentUsername = username;
		initialized = false;
	}
	if (!initialized) {
		refreshNotificationStore();
		initialized = true;
	}
	return notificationStore;
}

export async function refreshNotificationStore() {
	if (!currentUsername) return;
	try {
		const [replyNotifications, mentionNotifications] = await Promise.all([
			getAllReplyNotifications(currentUsername),
			getAllMentionNotifications(currentUsername)
		]);

		if (!replyNotifications || !mentionNotifications) {
			notificationStore.set([]);
			return;
		}
		if ('error' in replyNotifications && replyNotifications.error) {
			throw replyNotifications.error;
		}
		if ('error' in mentionNotifications && mentionNotifications.error) {
			throw mentionNotifications.error;
		}

		const allNotifications = [
			...(replyNotifications.data || []),
			...(mentionNotifications.data || [])
		];
		const uniqueNotifications: ExtendedNotification[] = [];

		allNotifications.forEach((notification) => {
			if (!uniqueNotifications.some((n) => n.id === notification.id)) {
				uniqueNotifications.push(notification as ExtendedNotification);
			}
		});

		uniqueNotifications.sort((a, b) => {
			const aTimestamp = new Date(a.created_at).getTime();
			const bTimestamp = new Date(b.created_at).getTime();
			return bTimestamp - aTimestamp;
		});

		notificationStore.set(uniqueNotifications);
	} catch (error) {
		console.error('Error:', error);
		notificationStore.set([]);
	}
}

export async function dismissAllNotifications() {
	notificationStore.update((notifications) => {
		notifications.forEach(async (notification) => {
			if (!notification.dismissed) {
				await markNotificationAsRead(notification.id);
				notification.dismissed = true;
			}
		});
		return notifications;
	});
}

export { notificationStore };
